/**
 * Optimized Slider for Products Carousel Section
 * Handles Drag-to-scroll and Interactive Progress Bar
 */
class ProductsCarouselSlider {
  constructor(element, options = {}) {
    this.container = element;
    this.list = element.querySelector('.slider-list') || element.querySelector('.slider');
    this.slides = this.list ? Array.from(this.list.children) : [];
    
    // Identify visual bars vs interaction containers
    this.progressContainers = options.progressBar ? [options.progressBar] : Array.from(element.querySelectorAll('.progress-container, .products-carousel-progress'));
    
    // If a container was passed, find the actual visual bar inside it
    if (options.progressBar) {
      const innerBar = options.progressBar.querySelector('.progress-bar, .products-carousel-progress-bar');
      this.progressBars = innerBar ? [innerBar] : [options.progressBar]; 
    } else {
      this.progressBars = Array.from(element.querySelectorAll('.progress-bar, .products-carousel-progress-bar'));
    }
    
    this.prevBtn = element.querySelector('.custom-slider-button--prev') || (this.container.closest('section')?.querySelector('.custom-slider-button--prev'));
    this.nextBtn = element.querySelector('.custom-slider-button--next') || (this.container.closest('section')?.querySelector('.custom-slider-button--next'));
    
    this.type = 'scroll';
    this.autoplay = element.dataset.autoplay === 'true';
    this.autoplayDelay = parseInt(element.dataset.autoplayDelay) || 5000;
    this.autoplayInterval = null;
    this.currentIndex = 0;
    
    this.isDragging = false;
    this.isVerticalScroll = false;
    this.startX = 0;
    this.scrollLeftStart = 0;
    this.dragThreshold = 5; // Lower threshold for responsiveness
    this.hasMoved = false;
    this.lastX = 0;

    this.init();
  }

  init() {
    if (!this.list) return;

    this.list.addEventListener('scroll', () => this.updateIndicators());

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scroll(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scroll(1));

    if (this.autoplay) {
      this.startAutoplay();
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => this.startAutoplay());
    }

    this.initDrag();
    this.initProgressBarInteraction();
    this.updateIndicators();

    window.addEventListener('resize', () => this.updateIndicators());
  }

  initProgressBarInteraction() {
    this.progressContainers.forEach(container => {
      container.style.cursor = 'pointer';

      // Click handling: maps 0-100% of the bar to slide indices
      // This matches the Professional Review section logic exactly
      container.addEventListener('click', (e) => {
        // Correctly check if the parent panel is active
        if (!this.container.classList.contains('active')) return;
        
        const rect = container.getBoundingClientRect();
        const clientX = e.clientX || (e.touches ? e.touches[0].clientX : 0);
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));

        if (this.list && this.slides.length > 0) {
          const targetIndex = Math.round(percentage * (this.slides.length - 1));
          
          // Centered alignment logic to match Categories Carousel
          const slide = this.slides[Math.max(0, targetIndex)];
          const targetLeft = slide.offsetLeft - (this.list.clientWidth / 2) + (slide.clientWidth / 2);
          const maxScroll = this.list.scrollWidth - this.list.clientWidth;
          
          this.smoothScrollTo(this.list, Math.max(0, Math.min(targetLeft, maxScroll)), 600);
        }
      });
    });
  }

  scroll(direction) {
    const items = Array.from(this.list.children);
    if (!items.length) return;
    
    const currentScroll = this.list.scrollLeft;
    const computedStyle = window.getComputedStyle(this.list);
    const paddingOffset = parseInt(computedStyle.scrollPaddingLeft) || 0;
    
    let currentIndex = 0;
    let minDiff = Infinity;
    
    items.forEach((item, index) => {
      const itemIdealScroll = item.offsetLeft - paddingOffset;
      const diff = Math.abs(itemIdealScroll - currentScroll);
      if (diff < minDiff) {
        minDiff = diff;
        currentIndex = index;
      }
    });

    let targetIndex = currentIndex + direction;
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    
    if (direction > 0 && currentScroll >= maxScroll - 5) {
      targetIndex = 0;
    } else if (direction < 0 && currentScroll <= 5) {
      targetIndex = items.length - 1;
    } else {
      targetIndex = Math.max(0, Math.min(targetIndex, items.length - 1));
    }

    const targetLeft = items[targetIndex].offsetLeft - paddingOffset;
    this.smoothScrollTo(this.list, targetLeft, 500);
  }

  smoothScrollTo(element, targetX, duration) {
    // DO NOT restore scroll-snap-type yet! Android Chrome will jerk if it's active during a smooth scroll.
    
    // Use native browser smooth scrolling instead of JS frame-by-frame calculations
    element.style.setProperty('scroll-behavior', 'smooth', 'important');
    element.scrollLeft = Math.round(targetX);
    
    // Clean up both overrides after the browser finishes the scroll
    setTimeout(() => {
      element.style.removeProperty('scroll-behavior');
      element.style.removeProperty('scroll-snap-type');
    }, 600);
  }

  updateIndicators() {
    if (this.progressBars.length > 0 && this.slides.length > 0) {
      const total = this.list.scrollWidth;
      const visible = this.list.clientWidth;
      const maxScroll = total - visible;
      
      let progress = '0%';
      if (maxScroll > 0) {
         const minWidth = (visible / total) * 100;
         const scrollPercent = this.list.scrollLeft / maxScroll;
         progress = (minWidth + (scrollPercent * (100 - minWidth))) + '%';
      } else {
         progress = '100%';
      }
      this.progressBars.forEach(bar => bar.style.width = progress);
    }
    
    let maxScroll = this.list.scrollWidth - this.list.clientWidth;
    if (maxScroll <= 0) {
      if (this.prevBtn) this.prevBtn.setAttribute('disabled', 'disabled');
      if (this.nextBtn) this.nextBtn.setAttribute('disabled', 'disabled');
    } else {
      if (this.prevBtn) {
        if (this.list.scrollLeft <= 5) this.prevBtn.setAttribute('disabled', 'disabled');
        else this.prevBtn.removeAttribute('disabled');
      }
      if (this.nextBtn) {
        if (this.list.scrollLeft >= maxScroll - 5) this.nextBtn.setAttribute('disabled', 'disabled');
        else this.nextBtn.removeAttribute('disabled');
      }
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => this.scroll(1), this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayInterval) clearInterval(this.autoplayInterval);
  }

  initDrag() {
    const list = this.list;
    
    // Bind handlers so they can be added/removed
    this.onMove = this.onMove || ((e) => {
      if (!this.isDragging || this.isVerticalScroll) return;
      
      const touch = e.touches ? e.touches[0] : null;
      const x = touch ? touch.pageX : e.pageX;
      const y = touch ? touch.pageY : e.pageY;
      
      const walk = (x - this.startX); 
      const walkY = (y - this.startY);

      // Axis detection: kill drag if vertical movement is dominant
      if (!this.hasMoved && Math.abs(walkY) > Math.abs(walk) * 1.5 && Math.abs(walkY) > this.dragThreshold) {
        this.isVerticalScroll = true;
        this.isDragging = false;
        list.classList.remove('is-dragging');
        return;
      }

      if (!this.hasMoved && Math.abs(walk) > this.dragThreshold) {
        this.hasMoved = true;
        list.classList.add('is-dragging');
        list.style.userSelect = 'none';
        list.style.webkitUserSelect = 'none';
        list.querySelectorAll('img, a').forEach(el => el.setAttribute('draggable', 'false'));
      }
      
      if (this.hasMoved) {
        list.scrollLeft = this.scrollLeftStart - walk;
        if (e.cancelable) e.preventDefault();
      }
      
      this.lastX = x;
    });

    this.onEnd = this.onEnd || ((e) => {
      if (!this.isDragging) return;
      this.isDragging = false;
      
      list.classList.remove('is-dragging');
      list.style.removeProperty('user-select');
      list.style.removeProperty('webkit-user-select');
      
      // We don't remove scroll-snap-type here yet, as snapToItem will handle its own override
      if (this.autoplay) this.startAutoplay();
      
      // Remove window listeners
      window.removeEventListener('mousemove', this.onMove);
      window.removeEventListener('mouseup', this.onEnd);

      if (!this.hasMoved) {
        list.style.removeProperty('scroll-behavior');
        list.style.removeProperty('scroll-snap-type');
        return;
      }

      const x = (e.pageX || (e.changedTouches ? e.changedTouches[0].pageX : this.lastX));
      const diff = x - this.startX;
      this.snapToItem(diff);
    });

    const onStart = (e) => {
      if (e.type === 'mousedown' && e.button !== 0) return;
      this.isDragging = true;
      this.hasMoved = false;
      this.isVerticalScroll = false;
      
      const touch = e.touches ? e.touches[0] : null;
      this.startX = touch ? touch.pageX : e.pageX;
      this.startY = touch ? touch.pageY : e.pageY;
      
      this.scrollLeftStart = list.scrollLeft;
      
      list.style.setProperty('scroll-behavior', 'auto', 'important');
      list.style.setProperty('scroll-snap-type', 'none', 'important');
      
      this.stopAutoplay();

      // Add window listeners for robust tracking
      if (e.type === 'mousedown') {
        window.addEventListener('mousemove', this.onMove);
        window.addEventListener('mouseup', this.onEnd);
      }
    };

    list.addEventListener('mousedown', onStart);
    list.addEventListener('touchstart', onStart, { passive: true });
    list.addEventListener('touchmove', this.onMove, { passive: false });
    list.addEventListener('touchend', this.onEnd);

    list.addEventListener('click', (e) => {
      if (this.hasMoved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
    list.addEventListener('dragstart', (e) => e.preventDefault());
  }

  snapToItem(dragDiff = 0) {
    const items = Array.from(this.list.children);
    const paddingOffset = parseInt(window.getComputedStyle(this.list).scrollPaddingLeft) || 0;
    
    // Find closest item to current scroll position instead of start position
    // This allows skipping multiple slides naturally
    const currentScroll = this.list.scrollLeft;
    
    let closestIndex = 0;
    let minDiff = Infinity;
    
    items.forEach((item, index) => {
      const itemIdealScroll = item.offsetLeft - paddingOffset;
      const diff = Math.abs(itemIdealScroll - currentScroll);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = index;
      }
    });

    // Momentum logic
    let targetIndex = closestIndex;
    const slidesToJump = 1; // Jump 1 slide at a time during drag

    if (Math.abs(dragDiff) > 10) { // Firm enough drag to trigger momentum
      if (dragDiff < 0 && targetIndex < items.length - 1) {
        // Dragged left (next)
        targetIndex = Math.min(items.length - 1, targetIndex + slidesToJump);
      } else if (dragDiff > 0 && targetIndex > 0) {
        // Dragged right (prev)
        targetIndex = Math.max(0, targetIndex - slidesToJump);
      }
    }
    
    targetIndex = Math.max(0, Math.min(targetIndex, items.length - 1));
    const targetLeft = items[targetIndex].offsetLeft - paddingOffset;
    this.smoothScrollTo(this.list, targetLeft, 500);
  }
}

class ProductsCarouselTabbedController {
  constructor(section) {
    this.section = section;
    this.tabButtons = section.querySelectorAll('.products-carousel-tabs__button');
    this.panels = section.querySelectorAll('.products-carousel-panel');
    this.progressBar = section.querySelector('.products-carousel-progress');
    this.sliders = new Map();
    this.activeSlider = null;
    this.init();
  }

  init() {
    this.tabButtons.forEach(btn => {
      btn.addEventListener('click', () => this.activateTab(btn, btn.getAttribute('data-target')));
    });

    const initialActive = this.section.querySelector('.products-carousel-panel.active');
    if (initialActive) {
      this.activeSlider = new ProductsCarouselSlider(initialActive, { progressBar: this.progressBar });
      this.sliders.set(initialActive.id, this.activeSlider);
    }
  }

  activateTab(button, targetId) {
    this.tabButtons.forEach(b => b.classList.remove('active'));
    this.panels.forEach(p => {
      p.classList.remove('active');
      if (p.id === targetId) {
        p.classList.add('active');
        if (this.activeSlider) this.activeSlider.stopAutoplay();
        let slider = this.sliders.get(targetId);
        if (!slider) {
          slider = new ProductsCarouselSlider(p, { progressBar: this.progressBar });
          this.sliders.set(targetId, slider);
        } else {
          slider.updateIndicators();
          if (slider.autoplay) slider.startAutoplay();
        }
        this.activeSlider = slider;
      }
    });
    button.classList.add('active');
  }
}

const initProductsCarousel = () => {
  document.querySelectorAll('.products-carousel').forEach(el => {
    if (el.dataset.productsSliderInitialized) return;
    new ProductsCarouselTabbedController(el);
    el.dataset.productsSliderInitialized = 'true';
  });
};

document.addEventListener('DOMContentLoaded', initProductsCarousel);
document.addEventListener('shopify:section:load', initProductsCarousel);
